import { Box } from '@primer/react';
import { getMatterById } from '@upright/common/model/input/materiality-assessment/matters';
import { MaterialityType } from '@upright/common/model/input/materiality-assessment/types';
import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import { LinkWithSearch } from '@upright/ui/common/generic/components/navigation/link-with-search';
import { useTheme } from '@upright/ui/common/generic/theme/custom-theme';
import { ReactNode } from 'react';

export const SingleMatrixMatter = (props: {
  matterId: SustainabilityMatterId;
  valence: 'pos' | 'neg' | 'posneg' | null;
  getSingleMatterViewUrl?: (matterId: SustainabilityMatterId, materialityType: MaterialityType) => string;
}) => {
  const { title, qualifier } = getMatterById(props.matterId);

  const noMatterUrl = !props.getSingleMatterViewUrl;

  if (noMatterUrl) {
    return <LinkContent title={title} qualifier={qualifier} valence={props.valence} />;
  }

  return (
    <LinkWithSearch
      to={props.getSingleMatterViewUrl ? props.getSingleMatterViewUrl(props.matterId, MaterialityType.IMPACT) : ''}
    >
      <LinkContent title={title} qualifier={qualifier} valence={props.valence} />
    </LinkWithSearch>
  );
};

export const SingleMatrixEntryWithValenceColor = (props: {
  valence: 'pos' | 'neg' | 'posneg' | null;
  children: ReactNode;
}) => {
  const { theme } = useTheme();
  const posColor = theme?.colorSchemes.light.colors.impact.positive.strong;
  const negColor = theme?.colorSchemes.light.colors.impact.negative.strong;
  const valence = props.valence;

  return (
    <Box display="flex" alignItems="flex-start" sx={{ gap: '0.5em' }}>
      <Box
        sx={{
          borderRadius: 3,
          width: '0.9em',
          height: '0.9em',
          flexShrink: 0,
          marginTop: '0.06em',
          background:
            valence === 'pos'
              ? posColor
              : valence === 'neg'
                ? negColor
                : `linear-gradient( -45deg, ${negColor}, ${negColor} 49%, white 49%, white 51%, ${posColor} 51% )`,
        }}
      />
      <Box as="span" fontSize={0} lineHeight="130%">
        {props.children}
      </Box>
    </Box>
  );
};

const LinkContent = ({
  title,
  qualifier,
  valence,
}: {
  title: string;
  qualifier?: string;
  valence: 'pos' | 'neg' | 'posneg' | null;
}) => {
  return (
    <SingleMatrixEntryWithValenceColor valence={valence}>
      <span>
        <span style={{ fontWeight: 500 }}>{title}</span>
        {qualifier ? ` (${qualifier})` : ''}
      </span>
    </SingleMatrixEntryWithValenceColor>
  );
};
